<template>
  <div>
    <v-navigation-drawer
      v-model="open"
      :width="$isMobile ? '100%' : '60%'"
      right
      fixed
      :permanent="open"
      hide-overlay
      class="z-index--100">
      <div class="ma-3">
        <div class="d-flex">
          <v-btn
            dark
            fab
            x-small
            outlined
            color="primary"
            class="mr-3"
            @click="closePanel"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="subtitle-1 primary--text my-auto">
            {{ $t('expenses.registerMovements.payOrders.drawer.title') }}
          </div>
        </div>
        <div>
          <div>
            <v-row class="pt-4">
              <v-col md="6" class="py-0">
                <v-combobox
                  data-cy="supplier-name"
                  v-model="$v.payOrderForm.supplier.$model"
                  :label="$t('expenses.registerMovements.payOrders.drawer.supplier')"
                  :search-input.sync="searchSupplier"
                  :loading="suppliers.loading"
                  :items="suppliers.data"
                  @input="$v.payOrderForm.supplier.$touch()"
                  @blur="$v.payOrderForm.supplier.$touch()"
                  :error-messages="supplierErrors"
                  item-text="name"
                  item-value="id"
                  dense
                  rounded
                  outlined>
                  <template v-slot:item="{ item }">
                    <div>{{ item.name }}</div>
                  </template>
                </v-combobox>
              </v-col>
              <v-col md="6" class="py-0">
                <v-checkbox
                  class="mt-0"
                  label="Marcar como anticipo o pago parcial"
                  v-model="payOrderForm.isAdvancePayment"
                  dense
                  color="primary"
                />
              </v-col>
              <v-col cols="12">
                <v-form v-model="isValid">
                  <v-data-table
                  :headers="table.headers"
                  :items="table.data"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 30, 40, 50]
                  }"
                  :items-per-page="10"
                  class="elevation-1 align-top">
                    <template v-slot:[`item.quantity`]="{ item }">
                      <div style="min-width: 80px;">
                        <v-text-field
                          rounded
                          outlined
                          dense
                          type="number"
                          v-model="item.quantity"
                          :rules="rules.productForm.quantity"
                          required>
                        </v-text-field>
                      </div>
                    </template>
                    <template v-slot:[`item.unit`]="{ item }">
                      <div style="max-width: 120px;">
                        <v-select
                          rounded
                          outlined
                          dense
                          item-text="name"
                          item-value="satCode"
                          :items="units"
                          :disabled="!units.length"
                          v-model="item.unit"
                          :rules="rules.productForm.unit"
                          required
                        />
                      </div>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                      <div style="min-width: 150px;">
                        <v-text-field
                          rounded
                          outlined
                          dense
                          v-model="item.name"
                          :rules="rules.productForm.name"
                          required>
                        </v-text-field>
                      </div>
                    </template>
                    <template v-slot:[`item.category`]="{ item }">
                      <div style="max-width: 130px;">
                        <v-select
                          rounded
                          outlined
                          dense
                          item-text="name"
                          item-value="_id"
                          :items="categories.data"
                          :disabled="!categories.data.length"
                          v-model="item.category"
                          :rules="rules.productForm.category"
                          required
                        />
                      </div>
                    </template>
                    <template v-slot:[`item.unitPrice`]="{ item }">
                      <div>
                        ${{ item.unitPrice }}
                      </div>
                    </template>
                    <template v-slot:[`item.amount`]="{ item }">
                      <div>
                        <span>
                          ${{ calculateAmount(item.quantity, item.unitPrice, item.discount) | toFix2 | numberWithComma }}
                        </span>
                      </div>
                    </template>
                    <template v-slot:[`item.discount`]="{ item }">
                      <div>
                        <span>
                          {{ item.discount | toFix2 | numberWithComma | prefix('$') }}
                        </span>
                      </div>
                    </template>
                  </v-data-table>
                </v-form>
                <v-btn
                  v-if="expensesPermissions['add-product-service']"
                  width="100%"
                  class="my-3"
                  color="primary"
                  depressed
                  @click="openAddProductDialog">
                  {{ $t('expenses.registerMovements.payOrders.drawer.buttonAdd') }}
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-row class="mx-0">
                  <v-col class="totalBox">
                    <v-row>
                      <v-col class="py-0">{{ $t('expenses.registerMovements.payOrders.drawer.subtotal') }}</v-col>
                      <v-col class="py-0 text-right">${{ payOrderSubtotal | toFix2 | numberWithComma }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0">{{ $t('expenses.registerMovements.payOrders.drawer.discount') }}</v-col>
                      <v-col class="py-0 text-right">${{ payOrderDiscount | toFix2 | numberWithComma }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0">{{ $t('expenses.registerMovements.payOrders.drawer.taxes') }}</v-col>
                      <v-col class="py-0 text-right taxesInput" style="text-align: right;">
                        <v-text-field
                          rounded
                          outlined
                          dense
                          v-model.lazy="$v.payOrderForm.taxes.$model"
                          type="number"
                          class="ma-0 pa-0"
                          prefix="$"
                          style="width: 250px; float: right;"
                          @input="$v.payOrderForm.taxes.$touch()"
                          @blur="$v.payOrderForm.taxes.$touch()"
                          :error-messages="taxesErrors"
                          required>
                        </v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0">{{ $t('expenses.registerMovements.payOrders.drawer.total') }}</v-col>
                      <v-col class="py-0 text-right">${{ payOrderTotal | toFix2 | numberWithComma }}</v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" class="d-flex justify-end">
                <v-btn
                  v-if="expensesPermissions['submit-pay-order']"
                  color="primary"
                  depressed
                  :disabled="!table.data.length || !isValid"
                  @click="addPayOrder">
                  {{ $t('expenses.registerMovements.payOrders.drawer.buttonCreate') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog @keydown.esc="addProductDialog=false" v-model="addProductDialog" persistent max-width="700px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('expenses.registerMovements.payOrders.drawer.dialog.title') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="addProductForm" v-model="productForm.valid">
            <v-container grid-list-xl>
              <v-layout wrap>
                <v-row class="pt-2">
                  <v-col md="6" class="py-0">
                    <v-text-field
                      rounded
                      outlined
                      dense
                      v-model.lazy="$v.productForm.quantity.$model"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.quantity')"
                      type="number"
                      @input="$v.productForm.quantity.$touch()"
                      @blur="$v.productForm.quantity.$touch()"
                      :error-messages="quantityErrors"
                      required>
                    </v-text-field>
                  </v-col>
                  <v-col md="6" class="py-0">
                    <v-select
                      rounded
                      outlined
                      dense
                      item-text="name"
                      item-value="satCode"
                      :items="units"
                      :disabled="!units.length"
                      v-model.lazy="$v.productForm.unit.$model"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.unit')"
                      @input="$v.productForm.unit.$touch()"
                      @blur="$v.productForm.unit.$touch()"
                      :error-messages="unitErrors"
                      required
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="6" class="py-0">
                    <v-text-field
                      rounded
                      outlined
                      dense
                      v-model.lazy="$v.productForm.name.$model"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.concept')"
                      @input="$v.productForm.name.$touch()"
                      @blur="$v.productForm.name.$touch()"
                      :error-messages="nameErrors"
                      required>
                    </v-text-field>
                  </v-col>
                  <v-col md="6" class="py-0">
                    <v-select
                      rounded
                      outlined
                      dense
                      item-text="name"
                      item-value="_id"
                      :items="categories.data"
                      :disabled="!categories.data.length"
                      v-model.lazy="$v.productForm.category.$model"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.category')"
                      @input="$v.productForm.category.$touch()"
                      @blur="$v.productForm.category.$touch()"
                      :error-messages="categoryErrors"
                      required
                    />
                  </v-col>
                  <v-col md="4" class="py-0">
                    <v-text-field
                      rounded
                      outlined
                      dense
                      v-model.lazy="$v.productForm.unitPrice.$model"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.cost')"
                      type="number"
                      prefix="$"
                      @input="$v.productForm.unitPrice.$touch()"
                      @blur="$v.productForm.unitPrice.$touch()"
                      :error-messages="unitPriceErrors"
                      required>
                    </v-text-field>
                  </v-col>
                  <v-col md="4" class="py-0">
                    <v-text-field
                      rounded
                      outlined
                      dense
                      v-model.lazy="$v.productForm.discount.$model"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.discount')"
                      type="number"
                      @input="$v.productForm.discount.$touch()"
                      @blur="$v.productForm.discount.$touch()"
                      :error-messages="discountErrors"
                      required>
                    </v-text-field>
                  </v-col>
                  <v-col md="4" class="py-0">
                    <v-text-field
                      rounded
                      outlined
                      dense
                      disabled
                      v-model.lazy="calculatedAmount"
                      :label="$t('expenses.registerMovements.payOrders.drawer.dialog.amount')"
                      type="number"
                      required>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="closeAddProductDialog">{{ $t('expenses.registerMovements.payOrders.drawer.dialog.buttonCancel') }}</v-btn>
          <v-btn color="primary" depressed @click="addProduct">{{ $t('expenses.registerMovements.payOrders.drawer.dialog.buttonAdd') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
// import { paymentForms } from '@/utils/constants'
import { validationMixin } from 'vuelidate'
import { required, decimal, minValue, between } from 'vuelidate/lib/validators'
import { debounce } from 'lodash'

export default {
  name: 'AddPayOrder',
  props: {
    isOpen: Boolean,
    transactions: {
      type: Array,
      default: () => []
    }
  },
  mixins: [validationMixin],
  validations: {
    productForm: {
      quantity: { required, decimal, minValue: minValue(0) },
      unit: { required },
      name: { required },
      category: { required },
      unitPrice: { required, decimal, minValue: minValue(0) },
      discount: { required, decimal, between: between(0, 100) }
    },
    payOrderForm: {
      // account: { required },
      // bank: { required },
      // paymentMode: { required },
      // paymentType: { required },
      supplier: { required },
      taxes: { required, decimal, minValue: minValue(0) }
    }
  },
  emits: ['updatePayOrders', 'updateSuppliers'],
  data () {
    return {
      overlay: false,
      addProductDialog: false,
      isValid: false,
      rules: {
        productForm: {
          quantity: [
            v => required(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.quantityErrorsReq'),
            v => decimal(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.quantityErrorsDec'),
            v => v > 0 || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.quantityErrorsMin')
            // { minValue: minValue(0) }
          ],
          unit: [v => required(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.unitErrorsReq')],
          name: [v => required(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.conceptErrorsReq')],
          category: [v => required(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.categoryErrorsReq')],
          unitPrice: [
            v => required(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.costErrorsReq'),
            v => decimal(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.costErrorsDec'),
            v => v > 0 || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.costErrorsMin')
          ],
          discount: [
            v => required(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.discountErrorsReq'),
            v => decimal(v) || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.discountErrorsDec'),
            v => v > 0 || this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.discountErrorsBet')
          ]
        }
      },
      table: {
        headers: [
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.quantity'), value: 'quantity', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.unit'), value: 'unit', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.concept'), value: 'name', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.category'), value: 'category', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.cost'), value: 'unitPrice', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.amount'), value: 'amount', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.payOrders.drawer.headers.discount'), value: 'discount', sortable: false }
        ],
        data: [],
        total: 0,
        itemsPerPage: 10,
        page: 1
      },
      payOrderForm: {
        valid: false,
        disabled: false,
        isEditing: false,
        // account: null,
        // bank: null,
        // paymentMode: null,
        // paymentType: null,
        supplier: null,
        taxes: 0,
        isAdvancePayment: false
      },
      productForm: {
        valid: false,
        disabled: false,
        isEditing: false,
        quantity: null,
        unit: null,
        name: null,
        category: null,
        unitPrice: null,
        discount: null
      },
      // accounts: {
      //   data: [],
      //   loading: false
      // },
      // banks: {
      //   data: [],
      //   loading: false
      // },
      // paymentForms: {
      //   data: paymentForms,
      //   loading: false
      // },
      // paymentTypes: {
      //   data: [],
      //   loading: false
      // },
      // units: {
      //   data: [],
      //   loading: false
      // },
      categories: {
        data: [],
        loading: false
      },
      suppliers: {
        data: [],
        loading: false
      },
      searchSupplier: ''
    }
  },
  watch: {
    searchSupplier: debounce(async function (val) {
      if (val && val.length > 3) {
        this.suppliers.loading = true
        try {
          const params = {
            // global: true,
            // skip: 0,
            // limit: 20
            name: `/${val}/i`,
            select: 'name'
          }
          const response = await this.$api.get(`/v1/restaurants/${this.$store.state.currentRestaurant.data._id}/providers`, params)
          if (response.data.length === 0) this.payOrderForm.supplier = val
          this.suppliers.data = response.data
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.suppliers.loading = false
      }
    }, 600),
    open: async function (val) {
      if (val) {
        await this.loadCategories({
          type: 'outgoing',
          restaurant: this.$store.state.currentRestaurant.data._id
        })
        await this.loadSuppliers()
        if (this.transactions && this.transactions.length) {
          this.addProductFromTransactions(this.transactions)
        }
      }
    }
  },
  computed: {
    open: {
      get: function () {
        return this.isOpen
      },
      set: function (newValue) {
        this.$emit('update:isOpen', newValue)
      }
    },
    units () {
      return this.$store.state.login.units.data
    },
    payOrderSubtotal () {
      return this.table.data.reduce((a, b) => a + parseFloat(b.amount) * parseFloat(b.quantity), 0)
    },
    payOrderDiscount () {
      return this.table.data.reduce((a, b) => parseFloat(a) + parseFloat(b.discount), 0)
    },
    payOrderTotal () {
      return parseFloat(this.payOrderSubtotal) - parseFloat(this.payOrderDiscount) + parseFloat(this.payOrderForm.taxes)
    },
    quantityErrors () {
      const errors = []
      if (!this.$v.productForm.quantity.$dirty) return errors
      !this.$v.productForm.quantity.minValue && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.quantityErrorsMin'))
      !this.$v.productForm.quantity.decimal && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.quantityErrorsDec'))
      !this.$v.productForm.quantity.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.quantityErrorsReq'))
      return errors
    },
    unitErrors () {
      const errors = []
      if (!this.$v.productForm.unit.$dirty) return errors
      !this.$v.productForm.unit.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.unitErrorsReq'))
      return errors
    },
    nameErrors () {
      const errors = []
      if (!this.$v.productForm.name.$dirty) return errors
      !this.$v.productForm.name.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.conceptErrorsReq'))
      return errors
    },
    categoryErrors () {
      const errors = []
      if (!this.$v.productForm.category.$dirty) return errors
      !this.$v.productForm.category.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.categoryErrorsReq'))
      return errors
    },
    unitPriceErrors () {
      const errors = []
      if (!this.$v.productForm.unitPrice.$dirty) return errors
      !this.$v.productForm.unitPrice.minValue && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.costErrorsMin'))
      !this.$v.productForm.unitPrice.decimal && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.costErrorsDec'))
      !this.$v.productForm.unitPrice.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.costErrorsReq'))
      return errors
    },
    discountErrors () {
      const errors = []
      if (!this.$v.productForm.discount.$dirty) return errors
      !this.$v.productForm.discount.between && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.discountErrorsBet'))
      !this.$v.productForm.discount.decimal && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.discountErrorsDec'))
      !this.$v.productForm.discount.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.dialog.discountErrorsReq'))
      return errors
    },
    supplierErrors () {
      const errors = []
      if (!this.$v.payOrderForm.supplier.$dirty) return errors
      !this.$v.payOrderForm.supplier.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.supplierErrorsReq'))
      return errors
    },
    // accountErrors () {
    //   const errors = []
    //   if (!this.$v.payOrderForm.account.$dirty) return errors
    //   !this.$v.payOrderForm.account.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.accountErrorsReq'))
    //   return errors
    // },
    // bankErrors () {
    //   const errors = []
    //   if (!this.$v.payOrderForm.bank.$dirty) return errors
    //   !this.$v.payOrderForm.bank.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.bankErrorsReq'))
    //   return errors
    // },
    // paymentModeErrors () {
    //   const errors = []
    //   if (!this.$v.payOrderForm.paymentMode.$dirty) return errors
    //   !this.$v.payOrderForm.paymentMode.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.paymentModeErrorsReq'))
    //   return errors
    // },
    // paymentTypeErrors () {
    //   const errors = []
    //   if (!this.$v.payOrderForm.paymentType.$dirty) return errors
    //   !this.$v.payOrderForm.paymentType.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.paymentTypeErrorsReq'))
    //   return errors
    // },
    taxesErrors () {
      const errors = []
      if (!this.$v.payOrderForm.taxes.$dirty) return errors
      !this.$v.payOrderForm.taxes.minValue && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.taxesErrorsMin'))
      !this.$v.payOrderForm.taxes.decimal && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.taxesErrorsDec'))
      !this.$v.payOrderForm.taxes.required && errors.push(this.$i18n.t('expenses.registerMovements.payOrders.drawer.taxesErrorsReq'))
      return errors
    },
    expensesPermissions () {
      return this.$store.state.login.permissions['expenses'].components['financial-movements'].actions
    },
    calculatedAmount () {
      return this.productForm.quantity * this.productForm.unitPrice - this.productForm.discount || 0
    }
  },
  mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  methods: {
    keyDownHandler (event) {
      if (event.code === 'Escape') {
        this.open = false
        this.resetPayOrderForm()
        this.resetProductForm()
        this.table.data = []
      }
    },
    // async loadAccounts (params = null) {
    //   params = params || {
    //     restaurant: this.$store.state.currentRestaurant.data._id
    //   }
    //   this.accounts.loading = true
    //   try {
    //     const response = await this.$api.get(`/v1/financial/unit-accounts`, params)
    //     this.accounts.data = response
    //   } catch (error) {
    //     this.$toasted.show(error.message, { duration: 3000 })
    //   }
    //   this.accounts.loading = false
    // },
    // async loadBanks (params = null) {
    //   params.restaurant = this.$store.state.currentRestaurant.data._id

    //   this.banks.loading = true
    //   try {
    //     const response = await this.$api.get(`/v1/financial/balance-accounts`, params)
    //     this.banks.data = response
    //   } catch (error) {
    //     this.$toasted.show(error.message, { duration: 3000 })
    //   }
    //   this.banks.loading = false
    // },
    // async loadPaymentTypes () {
    //   this.paymentTypes.loading = true
    //   try {
    //     const response = await this.$api.get(`/v1/catalog/sat/cfdi_metodos_pago`)
    //     this.paymentTypes.data = response
    //     // todo: select: id, texto
    //   } catch (error) {
    //     this.$toasted.show(error.message, { duration: 3000 })
    //   }
    //   this.paymentTypes.loading = false
    // },
    calculateAmount (quantity, unitPrice, discount) {
      return quantity * unitPrice - discount || 0
    },
    async loadCategories (params = null) {
      params = params || {
        type: 'outgoing',
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.categories.loading = true
      try {
        const response = await this.$api.get(`/v1/cashflows`, params)
        this.categories.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.categories.loading = false
    },
    async createPayOrder () {
      const isNewSupplier = typeof this.payOrderForm.supplier === 'string'
      const payload = {
        restaurant: this.$store.state.currentRestaurant.data._id,
        area: this.$store.state.area && this.$store.state.area.id,
        provider: isNewSupplier ? this.payOrderForm.supplier : this.payOrderForm.supplier.id,
        concepts: this.table.data.map(item => Object.assign(
          item,
          {
            amount: this.calculateAmount(item.quantity, item.unitPrice, item.discount),
            category: item.category,
            cashflow: item.category
          })
        ),
        providerName: isNewSupplier ? this.payOrderForm.supplier : this.payOrderForm.supplier.name,
        author: this.$store.state.login.user._id,
        amount: this.payOrderSubtotal,
        tax: this.payOrderForm.taxes,
        discount: this.payOrderDiscount,
        total: this.payOrderTotal,
        isAdvancePayment: this.payOrderForm.isAdvancePayment,
        bankTransactions: this.transactions.map(bt => bt._id)
      }
      this.overlay = true
      try {
        await this.$api.post(`/v1/expenses/${this.$store.state.currentRestaurant.data._id}`, payload)
        if (isNewSupplier) {
          this.$emit('updateSuppliers')
        }
        this.$emit('updatePayOrders')
        this.closePanel()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    resetPayOrderForm () {
      this.payOrderForm = {
        // account: null,
        // bank: null,
        // paymentMode: null,
        // paymentType: null,
        supplier: null,
        taxes: 0,
        isAdvancePayment: false
      }

      if (this.$refs.createPayOrderForm) this.$v.payOrderForm.$reset()
      this.payOrderForm.disabled = false
      this.payOrderForm.isEditing = false
    },
    resetProductForm () {
      this.productForm = {
        quantity: null,
        name: null,
        unit: null,
        category: null,
        unitPrice: null,
        discount: null
      }

      if (this.$refs.addProductForm) this.$v.productForm.$reset()
      this.productForm.disabled = false
      this.productForm.isEditing = false
    },
    openAddProductDialog () {
      this.addProductDialog = true
    },
    closeAddProductDialog () {
      this.addProductDialog = false
      this.resetProductForm()
    },
    addProduct () {
      this.$v.productForm.$touch()

      if (!this.$v.productForm.$invalid) {
        this.table.data.push({
          quantity: this.productForm.quantity,
          name: this.productForm.name,
          unit: this.productForm.unit,
          category: this.productForm.category,
          unitPrice: this.productForm.unitPrice,
          amount: this.calculateAmount,
          discount: this.productForm.discount
        })
        // this.payOrderForm.subtotal = table.data.reduce((a, b) => a.amount + b.amount, 0)
        // this.payOrderForm.discount = table.data.reduce((a, b) => a.discount + b.discount, 0)
        this.closeAddProductDialog()
      }
    },
    addProductFromTransactions (transactions) {
      // this.$v.productForm.$touch()

      this.table.data.push({
        quantity: 1,
        name: null,
        unit: null,
        category: null,
        unitPrice: transactions.reduce((acc, transaction) => acc + transaction.amount, 0),
        amount: transactions.reduce((acc, transaction) => acc + transaction.amount, 0),
        discount: 0
      })
      // if (!this.$v.productForm.$invalid) {
      //   // this.table.data.push({
      //   //   quantity: this.productForm.quantity,
      //   //   name: this.productForm.name,
      //   //   unit: this.productForm.unit,
      //   //   category: this.categories.data.find(x => this.productForm.category === x._id),
      //   //   unitPrice: this.productForm.unitPrice,
      //   //   amount: this.calculateAmount,
      //   //   discount: this.productForm.discount
      //   // })
      //   // this.payOrderForm.subtotal = table.data.reduce((a, b) => a.amount + b.amount, 0)
      //   // this.payOrderForm.discount = table.data.reduce((a, b) => a.discount + b.discount, 0)
      //   this.closeAddProductDialog()
      // }
    },
    addPayOrder () {
      this.$v.payOrderForm.$touch()

      if (!this.$v.payOrderForm.$invalid) {
        this.createPayOrder()
      }
      this.$emit('reset')
    },
    openPanel () {
      this.open = true
    },
    closePanel () {
      this.open = false
      this.resetPayOrderForm()
      this.resetProductForm()
      this.table.data = []
    },
    loadSuppliers: async function () {
      const params = {
        // fetchAll: true
      }
      this.suppliers.loading = true
      try {
        const response = await this.$api.get(`/v1/restaurants/${this.$store.state.currentRestaurant.data._id}/providers`, params)
        this.suppliers.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.suppliers.loading = false
    }
  }
}
</script>

<style lang="scss">
  .totalBox {
    border: 1px solid #ACABAB;
    border-radius: 13px;
    & .taxesInput .v-text-field input {
      text-align: right;
    }
  }
</style>
